import { fullStack } from "@/data/aiStack";

export const en = {
  scrollLabel: "scroll",

  nav: {
    web3: "WEB3",
    aiTeams: "AI TEAMS",
    oneToOne: "1:1 CONSULTING",
    letsTalk: "LET'S TALK",
    en: "EN",
    pl: "PL",
  },

  // ─── Gateway (/) ────────────────────────────────────────────────────────────
  gateway: {
    meta: {
      title: "Chris Scibiorek. Web3, AI, 1:1 Consulting.",
      description:
        "Web3 GTM consulting, AI for marketing teams, and 1:1 sessions for founders and senior leaders. 10 years in tech, 10 in Web3. 30+ projects.",
    },
    hero: {
      eyebrow: "CHRISWORKFLOWS.COM",
      headingLine1: "Strategy, AI and Web3.",
      headingHighlight: "Audit, AI workshop, or 1:1.",
      headingHighlight2: "Pick your path.",
      sub: "Three services, three kinds of clients. Start where it fits.",
      cta: "Explore services",
      ctaSecondary: "See what I do",
      availability: "Ready when you are",
      statsPills: [
        { value: "10", suffix: "y", label: "IN TECH" },
        { value: "30", suffix: "+", label: "PROJECTS SHIPPED" },
        { value: "3", suffix: "y", label: "AI-FIRST DAILY" },
      ],
    },
    flagship: {
      deliverables: [
        { icon: "Search", label: "GTM Roadmap" },
        { icon: "FileText", label: "Brand Narrative" },
        { icon: "Coins", label: "Tokenomics Review" },
        { icon: "TrendingUp", label: "Launch Strategy" },
      ],
    },
    cards: [
      {
        badge: "Core offer",
        eyebrow: "WEB3 / CRYPTO",
        title: "Web3 Consulting",
        desc: "GTM, brand, tokenomics, TGE, and AI implementation. For Web3 teams building projects that matter.",
        items: [
          "Brand & narrative audit",
          "Go-To-Market strategy",
          "TGE & listing readiness",
          "AI implementation audit",
        ],
        cta: "See the offer",
        href: "/web3",
      },
      {
        badge: "B2B",
        eyebrow: "AI IMPLEMENTATION",
        title: "AI for Marketing Teams",
        desc: "AI inside your marketing team. Pipelines, research, reporting, brand voice. For teams done doing manually what AI can handle.",
        items: [
          "Content production automation",
          "Research & competitor intel",
          "Reporting automation",
          "Brand voice enforcement",
        ],
        cta: "See what I do",
        href: "/ai-teams",
      },
      {
        badge: "50 min",
        eyebrow: "1:1 CONSULTING",
        title: "1:1 Consulting",
        desc: "50-minute sessions for founders and senior leaders. Bring your problem. We work through it together.",
        items: [
          "Founders & CEOs",
          "Senior leadership",
          "Positioning & strategy",
          "AI as thinking partner",
        ],
        cta: "Book a session",
        href: "/1-1",
      },
    ],
    about: {
      eyebrow: "ABOUT",
      heading: "10 years in tech, 10 in Web3",
      body: "3 years AI-first daily. 30+ projects at Cointelegraph Accelerator: TGE, tokenomics, CEX listings, DEX sale planning, go-to-market. Based in Poland, fully remote.",
      linkedinLabel: "Verify on LinkedIn \u2192",
      linkedinHref: "https://www.linkedin.com/in/krzysztofscibiorek",
    },
    stats: [
      { value: "10", suffix: "y", label: "in tech" },
      { value: "30", suffix: "+", label: "projects shipped" },
      { value: "3", suffix: "y", label: "AI-first daily" },
      { value: "100", suffix: "+", label: "advisory sessions" },
    ],
    footer: {
      heading1: "Ready to move?",
      headingHighlight: "Let's talk.",
      cta: "Start a conversation",
    },
  },

  // ─── AI for Marketing Teams (/ai-teams) ─────────────────────────────────────
  aiTeams: {
    meta: {
      title: "AI for Marketing Teams. Chris Scibiorek.",
      description:
        "AI inside your marketing team. Content production, research, reporting, brand voice. 10+ years in marketing. 30+ projects.",
    },

    hero: {
      tagline: "AI FOR MARKETING TEAMS",
      headingBefore: "Your marketing team has the talent.",
      headingBefore2: "",
      headingHighlight: "Add AI, unlock full potential",
      sub: "For marketing teams drowning in manual work. I find where your hours are leaking and replace them with AI. Your team actually uses it. 30+ projects. 10+ years in marketing.",
      ctaPrimary: "Book a discovery call",
      ctaSecondary: "See what I build",
      availability: "Ready when you are",
      statsPills: [
        { value: "60-80", suffix: "%", label: "CONTENT PRODUCTION TIME SAVED" },
        { value: "30", suffix: "+", label: "PROJECTS" },
        { value: "3-4x", suffix: "", label: "OUTPUT. NO NEW HIRES." },
      ],
      builtFor: "Built for",
      trustedBy: [
        "Marketing teams",
        "Content and growth teams",
        "Startups and scale-ups",
        "Small and mid-size businesses",
        "Solo marketing ops",
        "Brand and creative teams",
        "Product marketing teams",
        "Operations teams",
        "Founders running marketing",
      ],
    },

    services: {
      eyebrow: "For Marketing Teams",
      heading: "How I work with marketing teams",
      sub: "I find where your hours are leaking, then build AI systems that unlock your team's capacity, without hiring.",
      cards: [
        {
          title: "Marketing Workflow Audit",
          desc: "I map your marketing ops end to end: content, research, social, reporting. Then I point at exactly where hours are leaking and what AI can take off your plate.",
        },
        {
          title: "AI Implementation",
          desc: "I build AI automations inside your real marketing toolset: your briefs, your brand guidelines, your report formats. You learn the why, not just the how.",
        },
        {
          title: "Team Ownership",
          desc: "Your team walks out with a working system. No secrets. If someone forgets something, the docs are right there.",
        },
      ],
      price: "From $2,500",
      priceNote: "starting",
      tags: ["Web3 marketing teams", "Startup marketing ops", "Content-heavy agencies", "Founder-led marketing"],
      cta: "Start with a discovery call",
      noFees: "No hidden fees. Either side can pause or stop at any time.",
    },

    process: {
      eyebrow: "Process",
      heading: "From first call to running workflows in weeks",
      steps: [
        {
          num: "01",
          title: "Discovery Call",
          desc: "Tell me about your marketing: tools, team, content volume, reporting. 30 minutes. No pitch.",
        },
        {
          num: "02",
          title: "Marketing audit",
          desc: "I dig into your real workflows: how content gets made, where reporting breaks, where hours are leaking. You get a map of what AI can take off your plate.",
        },
        {
          num: "03",
          title: "Workshop sessions",
          desc: "We build the AI automations live, with your real docs and briefs. Your team is in the room, so they can keep running them after I leave.",
        },
        {
          num: "04",
          title: "Handoff & support",
          desc: "You walk out with working automations, clear docs, and optional support if you need me later.",
        },
      ],
    },

    results: {
      schematicHeader: "THE OUTCOMES",
      schematicSub: "WHAT YOUR TEAM GETS AFTER THE ROLLOUT",
      moduleLabels: ["", "", "", ""],
      coreIntelligence: "",
      metrics: [
        { value: "60-80%", label: "content production time saved" },
        { value: "30+", label: "projects delivered" },
        { value: "3-4x", label: "output. no new hires" },
        { value: "10+ years", label: "in marketing" },
      ],
    },

    fit: {
      eyebrow: "Fit",
      heading: "Is this for you?",
      goodFitTitle: "Good Fit",
      notFitTitle: "Not a Good Fit",
      goodFit: [
        {
          title: "Head of Marketing or founder who runs marketing",
          desc: "Responsible for content, campaigns, or growth and want to do more with the same team.",
        },
        {
          title: "Marketing team",
          desc: "You know your space and you're ready to use AI in production, not just in a playground.",
        },
        {
          title: "Tired of repetitive work eating your team's hours",
          desc: "Briefs, reporting, research, first drafts. The grind that shouldn't need humans.",
        },
        {
          title: "Want to understand the workflows, not just receive them",
          desc: "You want your team to be able to run and evolve the systems independently.",
        },
        {
          title: "Agency or content-heavy operation",
          desc: "High output, small team, and tired of doing it all by hand.",
        },
      ],
      notFit: [
        {
          title: "Looking for one-size-fits-all templates",
          desc: "I solve your real problems. I don't hand out generic prompt packs. That's not how AI learning works.",
        },
        {
          title: "Expecting results without team involvement",
          desc: "The team has to participate. That's what makes it stick.",
        },
      ],
    },

    about: {
      eyebrow: "About",
      heading: "10 years building, 3 years AI-first",
      paragraphs: [
        "10 years in crypto and Web3 as an operator: token launches, GTM strategies, and campaigns from seed stage to exchange listings. One launch hit +450% on listing day with $1.4M+ volume in the first 5 days.",
        "At Cointelegraph Accelerator: 30+ Web3 projects (GTM and TGE readiness), 100+ founder workshops, 50+ investor decks. I cut deck production time by ~70% without losing depth.",
        "Since 2022, I operate AI-first. Not one-shot prompting. I build context architecture: persistent memory, structured skill files, session protocols. The model handles synthesis. I handle direction, judgment, and framework design.",
        "Based in Poland, fully remote.",
      ],
      stats: [
        { value: "10", suffix: "", label: "Years in crypto & Web3" },
        { value: "30", suffix: "+", label: "Web3 projects shipped" },
        { value: "100", suffix: "+", label: "Workshops & advisory sessions" },
        { value: "50", suffix: "+", label: "Investor & launch decks built" },
        { value: "3", suffix: "+", label: "Years using AI daily" },
        { value: "~70%", suffix: "", label: "Deck production time cut with AI workflows" },
      ],
    },

    aboutMarketing: {
      eyebrow: "ABOUT",
      heading: "Marketing operator. AI native.",
      body: [
        "I've spent the last decade inside marketing teams at tech companies. I know where hours disappear, what breaks when you scale content, and which tools make the difference between a team that ships and a team that talks about shipping.",
        "Three years ago I stopped treating AI as a novelty and started building it into every workflow I touch. Today, AI runs underneath everything I do, for my own work and for the teams I build for.",
        "Based in Poland. Working fully remote with teams across Europe, US, and Asia.",
      ],
      credibility: [
        {
          title: "10+ Years in Marketing",
          desc: "Content, brand, growth, and ops inside tech startups, scale-ups, and agencies. Ten years of shipping real work.",
        },
        {
          title: "30+ Marketing Projects",
          desc: "I audit teams, design AI automations, and leave behind documentation the team can run on without me.",
        },
        {
          title: "AI-Native Operator",
          desc: "I use AI as a reasoning layer and execution engine, not a content shortcut.",
        },
      ],
      photoName: "Chris Scibiorek",
      photoRole: "AI Marketing Systems Strategist",
      linkedinLabel: "Verify on LinkedIn \u2192",
    },

    stack: {
      eyebrow: "TOOLS I USE",
      heading: "Tools I use daily",
      items: [...fullStack],
    },

    aiSystems: {
      eyebrow: "How I use AI",
      heading: "Systems I built and use daily",
      desc: "I do not recommend AI tools. I build production systems with them. This is what runs behind my work every day, and what I deploy for clients.",
      tabs: [
        {
          id: "agentic",
          short: "AI that remembers everything",
          label: "Agentic work environment",
          filename: "context-architecture.yml",
          description:
            "A multi-layered AI system with persistent memory, skill files, and session protocols. Engineered as infrastructure, not one-off prompts.",
          bullets: [
            "Context carries across sessions: no re-explaining, no lost progress",
            "Skill files encode repeatable processes (GTM launches, deck builds, reporting)",
            "Teams replicate this architecture for their own workflows. Nothing magic here, just well-built infrastructure.",
          ],
        },
        {
          id: "content",
          short: "Content in minutes, not hours",
          label: "Multi-step content pipeline",
          filename: "pipeline.config",
          description:
            "From brief to publish-ready in minutes. Every step is automated, with a human review gate only where it matters.",
          bullets: [
            "Brief parsing, research pull, draft generation, and review in one flow",
            "AI knows your brand. Every piece sounds like you.",
            "Content production 80% faster, often with better quality than manual work.",
          ],
        },
        {
          id: "journal",
          short: "Decisions grounded in context",
          label: "AI-powered daily journal",
          filename: "journal-protocol.md",
          description:
            "Daily check-ins that flow straight into project memory and decision logs. Context compounds, so decisions get sharper.",
          bullets: [
            "Morning plan, midday pulse, evening review. Three check-ins a day.",
            "Decisions and insights auto-logged into persistent memory",
            "You end up knowing why you're doing what you're doing. And you can explain it.",
          ],
        },
        {
          id: "dedicated-dashboards",
          short: "Your command station",
          label: "Dedicated Dashboards",
          filename: "DedicatedDashboard.md",
          description:
            "Custom HTML dashboards built for specific projects. Think of it as a Trello or Asana, but shaped exactly around your workflow.",
          bullets: [
            "Single-file custom interfaces (HTML/JS) without bloated SaaS subscriptions",
            "A dedicated environment for a specific topic, shaped around your process.",
            "A fully customizable setup. You change colors, add columns, add anything you want. Nothing in the way.",
          ],
        },
      ],
    },

    booking: {
      eyebrow: "Book",
      heading: "Start with a Discovery Call",
      discoveryNote:
        "Book a 30-minute Discovery Call. No pitch. We'll talk through your marketing and see if we're a fit.",
      fallback: "Having trouble booking?",
      fallbackLink: "Message me on LinkedIn →",
    },

    faq: {
      eyebrow: "Questions",
      heading: "FAQ",
      items: [
        {
          q: "How is this different from hiring an AI consultant who teaches prompting?",
          a: "Prompting is one layer. I build the architecture underneath it: brand voice files, repeatable processes, structured workflows that produce the same output every time without re-prompting. Your team learns a system, not a trick.",
        },
        {
          q: "How long does it take to see results?",
          a: "Most teams see real time savings within the first two weeks. Full implementation across a marketing team typically takes 4-6 weeks, depending on size and appetite.",
        },
        {
          q: "Does my team need AI experience to start?",
          a: "Not really. A team with zero AI exposure is often easier to work with than one full of half-learned habits. We start from scratch, and by the end of the workshops your team is running the systems themselves.",
        },
      ],
      panelEyebrow: "STILL EVALUATING?",
      panelHeading: "Let me show you where AI fits.",
      panelBody: "A 30-minute Discovery Call. I look at your current marketing and flag where AI actually fits. No pitch.",
      panelCta: "Book a Discovery Call",
    },

    footer: {
      heading1: "Your marketing team is leaving hours on the table.",
      headingHighlight: "Let's fix that.",
      cta: "Let's talk",
    },

    advisoryTeaser: {
      eyebrow: "For Individual Leaders",
      heading: "Working solo or need strategic input?",
      body: "50-minute sessions for founders and senior leaders. Bring your problem. We work through it together.",
      ctaLabel: "See advisory sessions",
      meta: "$150 / session · Usually within the same week",
    },
  },

  // ─── Web3 / Crypto Consulting (/web3) ──────────────────────────────────
  web3audit: {
    meta: {
      title: "Web3 GTM Consulting. Chris Scibiorek.",
      description:
        "Full-picture audits: brand, GTM, tokenomics, AI implementation. 10 years in Web3. 30+ projects at Cointelegraph Accelerator.",
    },
    hero: {
      eyebrow: "WEB3 / CRYPTO CONSULTING",
      availability: "Ready when you are",
      headingLine1: "Your project has",
      headingLine2: "gaps",
      headingHighlight: "costing you momentum.",
      headingHighlight2: "",
      sub: "Full-picture audits: Brand, GTM, Tokenomics, and AI implementation. Framework refined across 30+ projects at Cointelegraph Accelerator.",
      meta: "Project-based · From $2,500",
      ctaPrimary: "Book a Discovery Call",
      ctaSecondary: "See what's covered",
      statsPills: [
        { value: "10", suffix: "y", label: "IN WEB3" },
        { value: "30", suffix: "+", label: "PROJECTS SHIPPED" },
        { value: "6", suffix: "", label: "CORE PILLARS" },
      ],
    },
    whatIAudit: {
      eyebrow: "Scope",
      heading: "What the audit covers",
      headingPrefix: "What the ",
      headingGradient: "audit",
      headingSuffix: " covers",
      items: [
        {
          title: "Brand & Narrative",
          desc: "Are you speaking to investors and users in their language? Is your story clear and consistent? I check your website, whitepaper, socials, and pitch deck.",
        },
        {
          title: "Go-To-Market Strategy",
          desc: "Do you have specific KPIs? A 30 and 90-day plan? What happens before the listing, and after? Who are you actually talking to, and how?",
        },
        {
          title: "Tokenomics & Listing Readiness",
          desc: "Is your tokenomics well-designed? Have you talked to exchanges yet, which ones? Is selling pressure properly distributed? Is vesting long enough?",
        },
        {
          title: "Community & Content",
          desc: "Do you have a content strategy, or are you posting to post? Does your calendar support the narrative or dilute it?",
        },
        {
          title: "AI Implementation",
          desc: "Are your competitors using AI where you aren't? I map your current usage and show where to start.",
        },
        {
          title: "Workflow & Process",
          desc: "Where are the bottlenecks slowing your team down? I'll tell you where your time is leaking, and how to fix it.",
        },
      ],
    },
    whoFor: {
      eyebrow: "WHO IT'S FOR",
      heading: "For founders who want the bigger picture",
      items: [
        {
          title: "Pre-TGE Founders",
          desc: "For founders who need an outside view.",
        },
        {
          title: "Post-Launch Teams",
          desc: "For teams who know something isn't working but can't pinpoint what.",
        },
        {
          title: "Strong Product, Weak GTM",
          desc: "For strong products with weak strategy and unclear positioning.",
        },
      ],
    },
    whatYouGet: {
      eyebrow: "WHAT YOU GET",
      heading: "What you actually get",
      sub: "NO FLUFF. JUST ACTION.",
      items: [
        {
          title: "Full Report",
          desc: "A diagnostic of your current GTM, brand, and operations. Written so you and your team can act on it, not a pile of pages no one reads.",
        },
        {
          title: "Prioritized Roadmap",
          desc: "A 60-day action plan your team can start executing the next day. Priorities first, nice-to-haves last.",
        },
        {
          title: "Debrief",
          desc: "A call with your founders and leads. We walk through every finding and answer 'so how do we actually fix it?'",
        },
        {
          title: "Straight Answer",
          desc: "I'll tell you straight if the tech is weak or the market doesn't want your product. No sugar-coating.",
        },
      ],
    },
    process: {
      eyebrow: "",
      heading: "Process",
      steps: [
        {
          label: "Research",
          body: "Before we talk, I run a quick analysis: website, token page, Twitter/X, LinkedIn, content, community.",
        },
        {
          label: "Discovery Call",
          body: "30 minutes. You tell me where you are, what you need, and what you already know. I listen more than I talk.",
        },
        {
          label: "Deep Dive",
          body: "60 minutes with you or your team. We talk specifics: what works, what's breaking, what nobody's saying out loud. Getting to know each other.",
        },
        {
          label: "Report & Debrief",
          body: "3-5 days to write it up: gap analysis and roadmap. Then a debrief where you'll know exactly what to do next, and why.",
        },
      ],
    },
    whyMe: {
      eyebrow: "BACKGROUND",
      heading: "Why work with me",
      credibility: [
        {
          title: "10 Years in Crypto",
          desc: "10 years of market cycles, narrative shifts, and watching what actually works. Not a consultant who learned Web3 last year.",
        },
        {
          title: "30+ Projects Shipped",
          desc: "At Cointelegraph Accelerator and through independent consulting. TGE, listings, GTM. All shipped.",
        },
        {
          title: "GTM Specialist",
          desc: "I know what works and what doesn't, across DeFi, GameFi, L1/L2, and RWA.",
        },
      ],
      closer:
        "Web3 is the only industry I've worked in.\n10 years from the inside, not the outside.",
      photoName: "Chris Scibiorek",
      photoRole: "Web3 GTM Strategist",
      linkedinVerifyLabel: "Verify on LinkedIn →",
    },
    notFit: {
      eyebrow: "Fit",
      heading: "When this isn't a fit",
      items: [
        "Purely speculative projects or memecoins without real product substance",
        "Teams without a decision-maker available to provide context during the deep dive",
        "Projects with no urgency to act on findings. The roadmap is only as useful as what you do with it",
      ],
    },
    pricing: {
      eyebrow: "Pricing",
      heading: "Let's scope it",
      body: "Every project is different. Scope and depth depend on your project and what you need most. Starting at $2,500. Book a discovery call, no commitment.",
    },
    book: {
      eyebrow: "Book",
      heading: "Start with a discovery call",
      discoveryNote:
        "Book a 30-minute discovery call. No pitch. We walk through your project and check if there's a fit.",
      fallback: "Having trouble booking?",
      fallbackLink: "Message me on LinkedIn →",
    },
    scope: {
      eyebrow: "GET STARTED",
      heading: "Let's scope it",
      rows: [
        { label: "Full Web3 GTM Audit", value: "From $2,500" },
        { label: "GTM Architecture & Roadmap", value: "Included" },
        { label: "Tokenomics Stress Test", value: "Add-on" },
        { label: "Advisory Retainer (Monthly)", value: "Custom quote" },
      ],
      footnote: "For scope beyond the standard audit, get in touch for project-based pricing.",
      bookCardTitle: "Book a Discovery Call",
      bookCardDesc: "30 minutes. We'll talk through your project and see if we're a fit.",
      bookCardCta: "Open Calendar",
      fallback: "Having trouble booking?",
      fallbackLink: "Message me on LinkedIn →",
    },
    builtFor: {
      eyebrow: "WHO I WORK WITH",
      heading: "Teams I work with",
      items: [
        "Pre-TGE Web3 founders",
        "DeFi protocols",
        "L1/L2 ecosystems",
        "Web3 marketing agencies",
        "Venture Capitals",
        "RWA",
        "AI x Crypto projects",
      ],
    },
    faq: {
      eyebrow: "Questions",
      heading: "FAQ",
      items: [
        {
          q: "How long does the full process take?",
          a: "Usually 2-3 weeks. Length depends on the scale of the work and how quickly you can make time for the Deep Dive.",
        },
        {
          q: "Is this only for pre-launch projects?",
          a: "No. Post-launch audits are often the most useful ones. You have real users, real data, and real feedback to work with. Pre-launch sets the foundations; post-launch is where I catch what's actually breaking.",
        },
        {
          q: "What do you actually get?",
          a: "A written gap analysis by area, a prioritized action list with the reasoning behind every item, and a live debrief where we go through everything together.",
        },
        {
          q: "My project is on a specific chain. Do you have experience there?",
          a: "The methodology works regardless of chain. GTM, brand, and tokenomics principles don't change by chain. Across 30+ projects I've covered most major ones.",
        },
      ],
      panelEyebrow: "STILL HAVE QUESTIONS?",
      panelHeading: "Let's talk it through.",
      panelBody: "A 30-minute Discovery Call. No pitch. I'll help you scope the problem even if we don't end up working together.",
      panelCta: "Book a Discovery Call",
    },
    footer: {
      heading1: "Your project deserves an outside view.",
      headingHighlight: "Let's find what's missing.",
      cta: "Book a discovery call",
    },
  },

  // ─── 1:1 Consulting (/1-1) ─────────────────────────────────────────────────
  oneToOne: {
    meta: {
      title: "1:1 Consulting. Chris Scibiorek.",
      description:
        "50-minute 1:1 sessions for founders and senior leaders. Bring one real decision. Leave with a direct recommendation backed by 10 years in Web3 and GTM, and 3 years working with AI every day.",
    },
    hero: {
      eyebrow: "1:1 CONSULTING",
      headingBefore: "Clarity for founders",
      headingHighlight: "and senior leaders.",
      sub: "50 minutes. You bring your problem. I bring 10 years in Web3 and GTM, plus 3 years building with AI every day. We go through it together, and you leave knowing what to do next.",
      meta: "50-minute session",
      ctaPrimary: "Book a session",
      ctaSecondary: "See how it works",
      availability: "Available Now",
      photoStatsBadge: {
        value: "10 Years",
        label: "EXPERIENCE",
      },
    },
    supportingLineParts: [
      { text: "Your ", highlight: false },
      { text: "decision", highlight: true },
      { text: ", your ", highlight: false },
      { text: "context", highlight: true },
      { text: ", your ", highlight: false },
      { text: "next step", highlight: true },
      { text: ".", highlight: false },
    ] as { text: string; highlight: boolean }[],
    triptych: [
      {
        label: "Your decision",
        desc: "We solve a real problem together. I've run workshops, trainings and strategy sessions across 30+ projects. I've seen what works and what doesn't.",
        bottomLabel: "01 REAL CHALLENGE",
      },
      {
        label: "Your context",
        desc: "I read your context before the session. All 50 minutes go to your problem, not onboarding.",
        bottomLabel: "02 PREPARATION",
      },
      {
        label: "Your next step",
        desc: "You leave with one clear recommendation. Not a list of options.",
        bottomLabel: "03 CLEAR DIRECTION",
      },
    ],
    whoFor: {
      eyebrow: "WHO THIS IS FOR",
      heading: "For leaders with real decisions to make",
      items: [
        {
          title: "Founders needing outside eyes",
          desc: "Fresh perspective on what you can't see from inside your own team.",
        },
        {
          title: "Leaders facing a hard call",
          desc: "Pivot, launch, hiring, restructure. You want to pressure-test it before you commit.",
        },
        {
          title: "Teams implementing AI",
          desc: "You want AI woven into your real work. I show you how to do it, on your actual problem.",
        },
      ],
      closer: "",
    },
    workOn: {
      eyebrow: "SESSION TOPICS",
      heading: "What we can work on together",
      items: [
        "Go-to-market strategy and launch sequencing",
        "Brand positioning and narrative",
        "AI woven into your real work and your team's workflows",
        "Team design: who to hire, when, and for what",
        "Pitch deck or investor story review",
        "Prep for a board, investor, or team conversation",
      ],
      closer: "We can go deep on one challenge, or cover a few. You decide.",
    },
    howItWorks: {
      eyebrow: "",
      heading: "The Session",
      steps: [
        {
          label: "Discovery Call",
          body: "Book a 30-minute intro. No pitch, no commitment. We check if there's a clear problem worth a full session.",
        },
        {
          label: "Before the session",
          body: "After you book, send me a short note: what you're working through and any context I should have. The more specific, the better the session.",
        },
        {
          label: "During the session",
          body: "We break your situation down piece by piece. I tell you what I'd do and why. If AI helps solve it, I show you how to use it on your actual problem.",
        },
        {
          label: "After the session",
          body: "You leave with a clear direction, a direct recommendation, and the reasoning behind it, written down.",
        },
      ],
    },
    expectations: {
      eyebrow: "Outcomes",
      heading: "What you can expect",
      items: [
        "A direct recommendation on what to do next.",
        "Honest feedback: the kind a friend probably wouldn't give you.",
        "Pattern recognition and the likely chain of cause and effect.",
        "A real action plan. Not a pat on the back.",
      ],
    },
    whyMe: {
      eyebrow: "BACKGROUND",
      heading: "Why work with me",
      credibility: [
        {
          title: "10 Years in Web3 and Crypto",
          desc: "I've shipped TGEs, listings, GTMs and tokenomics across 30+ projects. I analyzed the problem from the inside, not from the outside.",
        },
        {
          title: "30+ Projects Across Stages",
          desc: "TGE, CEX/DEX listings, GTM strategy, tokenomics, team building. Pre-seed to pre-Series A.",
        },
        {
          title: "3 Years AI-Native",
          desc: "I work AI-first every day. On the session I show you how to weave AI into your real work, live, on your actual problem.",
        },
      ],
      closer:
        "Most consultants give you a framework. I give you the answer to your problem.",
      photoName: "Chris Scibiorek",
      photoRole: "1:1 Consulting",
      linkedinVerifyLabel: "Verify on LinkedIn →",
    },
    fit: {
      eyebrow: "FIT",
      heading: "Is this a good fit?",
      goodFitLabel: "Good fit",
      goodFit: [
        "You are a founder, CEO, or senior leader with an important decision to make",
        "Value direct input and can handle honest feedback",
        "Want an experienced operator's take, not a consultant's framework",
        "Are open to being challenged on your current direction",
      ],
      lessFitLabel: "Not for you",
      lessFit: [
        "Looking for general AI tool recommendations or a tools roundup.",
        "Want someone to take the decision off your hands.",
      ],
    },
    book: {
      eyebrow: "Book",
      heading: "Book a session",
      formatItems: ["50-minute 1:1 session", "Online call"],
      price: "$150",
      priceNote: "per session",
      payNote: "",
      discoveryNote:
        "Start with a 30-minute Discovery Call. No commitment. Just checking if we're on the same wavelength.",
      fallback: "Having trouble booking?",
      fallbackLink: "Message me on LinkedIn →",
    },
    tools: {
      eyebrow: "How I work",
      heading: "AI on your problem",
      desc: "This isn't just strategy. I show you how to use AI in your own work, on your real problem, not a generic walkthrough. You leave knowing what to run tomorrow.",
    },
    faq: {
      eyebrow: "Questions",
      heading: "Frequently asked",
      items: [
        {
          q: "What should I bring to the session?",
          a: "A real decision or challenge you're facing. The more specific, the more useful. Share context beforehand so we can use the full 50 minutes on the substance.",
        },
        {
          q: "Is this useful for Web3 founders specifically?",
          a: "Yes. 9 years in crypto means I know the patterns: TGE failures, GTM mistakes, narrative gaps, tokenomics traps. If you're building in Web3, that experience is directly relevant.",
        },
        {
          q: "Can we cover more than one topic?",
          a: "One main challenge per session works best. Going deep on one decision is more useful than surface-level on three. Multiple sessions are available if needed.",
        },
        {
          q: "How is this different from a coaching session?",
          a: "Coaching facilitates your thinking. This session gives you a direct recommendation from someone with relevant experience. I'll tell you what I'd do and why. You decide what to do with it.",
        },
        {
          q: "Do I need to come with a fully defined problem?",
          a: "No. 'I know something isn't working but I can't put my finger on it' is a valid starting point. Part of what I do is help identify the real question before we answer it.",
        },
      ],
    },
    footer: {
      heading1: "Ready to think it through?",
      headingHighlight: "Book a session.",
      cta: "Book a session",
    },
  },

  // ─── Shared Contact Form ─────────────────────────────────────────────────────
  contactForm: {
    heading: "Have a question? Ask directly.",
    subheading: "For anything that doesn't need a call yet. I reply within 24h.",
    toggleLabel: "Have a question? Send it here",
    fields: {
      name: {
        label: "First name",
        placeholder: "Alex",
      },
      email: {
        label: "Email",
        placeholder: "you@company.com",
      },
      company: {
        label: "Company or project",
        placeholder: "Optional",
      },
      question: {
        label: "Your question",
        placeholder: "Ask me anything.",
        maxLength: 500,
      },
    },
    submit: "Send",
    sending: "Sending...",
    success: "Got it. I'll reply within 24h.",
    error: "Something went wrong. Message me on LinkedIn instead.",
  },

  // ─── Hire (/hire) ──────────────────────────────────────────────────────────
  hire: {
    meta: {
      title: "Chris Scibiorek. AI-Native GTM and Growth Strategist.",
      description: "10 years in tech, 10 in Web3 and crypto. 30+ projects from accelerator to listing. Available now for senior remote roles.",
    },
    hero: {
      availability: "Available Now",
      name: "CHRIS SCIBIOREK",
      subtitle: "AI-Native GTM and Growth Strategist",
      body: "I don't just execute strategy. I build systems that make strategies credible. 10 years in tech. 10 in Web3 and crypto. AI as an operating layer, not a tool.",
      roleBadges: [
        "GTM STRATEGY",
        "AI WORKFLOWS",
        "GROWTH SYSTEMS",
      ],
      statBadgeLeft: {
        value: "10 Years",
        label: "IN TECH/WEB3/CRYPTO",
      },
      statBadgeRight: {
        value: "30+",
        label: "PROJECTS SHIPPED",
      },
      bottomBar: {
        item1: "10 Years Tech · 3 yrs AI-first",
        item2: "Available Now · Full-time",
        item3: "Poland · Fully Remote",
        cta: "Get in touch",
      },
    },
    tldr: {
      heading: "TL;DR for Hiring Teams",
      items: [
        { title: "10 Years in Tech", desc: "10 of them in Web3 and crypto. Operator, not a consultant who learned the space last year." },
        { title: "30+ Projects Shipped", desc: "From pre-TGE to post-listing. Cointelegraph Accelerator and direct mandates." },
        { title: "AI-Native Operator", desc: "3 years building AI workflows into every engagement. Not prompt engineering. System design." },
        { title: "Available Now, Remote", desc: "Poland-based, fully remote, open to senior roles across EU, US, and APAC." },
        { title: "2-3 Parallel Roles", desc: "Fractional and full-time considered. I execute at the pace of a small team." },
        { title: "Works Fast, Automates", desc: "Every engagement produces automation artifacts your team keeps and operates." },
      ],
    },
    operatingModel: {
      eyebrow: "HOW I WORK",
      heading: "How I work",
      principles: [
        { title: "AI-First", desc: "I start every problem with AI. A good prompt + good context = an answer to almost anything." },
        { title: "Systems over one-offs", desc: "What I build once keeps running. Every engagement leaves a system behind, not a one-time deliverable." },
        { title: "High-Agency", desc: "Ownership over delegation. I solve the problem, not route it." },
        { title: "Ambiguity-Ready", desc: "I start from unclear briefs and messy context. What comes out is a plan and a shipped outcome." },
        { title: "Human-Led Judgment", desc: "AI synthesis, human direction. The thinking is mine." },
        { title: "Outcome-Oriented", desc: "I measure results, not activity." },
      ],
    },
    whatIDeliver: {
      eyebrow: "WHAT I DELIVER",
      heading: "What I deliver",
      metrics: [
        { value: "30+", title: "Go-to-Market Systems", desc: "Architected from scratch for Tier 1 Web3 protocols." },
        { value: "~70%", title: "Efficiency Gain", desc: "Through AI-powered content production and automation." },
        { value: "100%", title: "Delivered to listing", desc: "Every token launch I led made it to exchange listing on schedule." },
      ],
    },
    professionalOdyssey: {
      eyebrow: "EXPERIENCE",
      heading: "Experience",
      items: [
        { dateRange: "Sep 2024 - Mar 2026", role: "Cointelegraph Accelerator", desc: "GTM & Growth Marketing Lead. Led GTM across 30+ Web3 projects, delivered 100+ founder workshops." },
        { dateRange: "Jan 2024 - Sep 2024", role: "Pixel Trapps", desc: "Token Launch & GTM Lead. Delivered +450% on listing day and $1.4M+ volume in the first 5 days." },
        { dateRange: "Apr 2023 - Dec 2023", role: "Empeiria", desc: "Founding Growth Marketing Lead. Built go-to-market from zero." },
        { dateRange: "Jan 2023 - Apr 2023", role: "3X Capital", desc: "Investment Analyst. Evaluated crypto-native founders and early-stage investment theses." },
        { dateRange: "Mar 2022 - Dec 2022", role: "Norion", desc: "Tokenomics Specialist. Designed token supply and incentive systems for launch." },
        { dateRange: "Apr 2020 - Feb 2022", role: "CloudBest", desc: "Blockchain & Crypto Manager. Watched the company scale from 20 to 280 people. Learned how to operate in a fast-changing environment." },
        { dateRange: "Mar 2019 - Mar 2020", role: "MineBest", desc: "R&D Marketing Specialist. Learned how “bitcoin mining” actually works from the inside." },
        { dateRange: "Jul 2018 - Jan 2019", role: "indaHash", desc: "Crypto Communication Specialist. First crypto role. Foundations of influencer marketing and working in a startup." },
      ],
    },
    whatIBuildWithAI: {
      titleLine1: "What I Build",
      titleLine2: "With AI",
      blocks: [
        { title: "AI environments with persistent memory", desc: "I build multi-layer AI systems with skill files, session protocols and long-term memory. Context carries across sessions, nothing gets re-explained, nothing gets lost." },
        { title: "Content pipelines, 80% faster", desc: "I build end-to-end content systems: brief to publish-ready in minutes. Every step automated, human review exactly where it matters. 80% faster than manual, often better quality." },
        { title: "Daily AI journaling systems", desc: "I built a 3-a-day check-in system (morning plan, midday pulse, evening review) that flows straight into project memory. Decisions compound over time, and I can explain why I made every one." },
        { title: "Custom HTML dashboards", desc: "I build single-file dashboards for specific projects: Trello or Asana shaped around one workflow, not the other way around. No bloated SaaS subscriptions, no features you don't use." },
      ],
      stackTitle: "MY TECH STACK",
      stack: [...fullStack],
      networkLabel: "AI NETWORK",
    },
    testimonials: {
      eyebrow: "SOCIAL PROOF",
      heading: "What colleagues say",
      items: [
        {
          name: "Iraklis Anastasiou",
          role: "Research Analyst & Writer @CoinTelegraph",
          quote: "Chris is a multifaceted individual. He is one of the few people I've met in a long time who has an amicable, infectiously positive, and ethical personality. His genuine character makes him easy to talk to and work with, and his deep knowledge of various subjects makes him a most valuable asset in any organization. He has a natural talent for learning anything and everything, consistently preparing highly thought-out and analytical work with meticulous and methodical quality. Pairing his battle-tested experience across a number of roles with his expertise in AI-driven automations and efficiencies, he is truly a one-man department. I highly recommend Chris for any senior management-level role and above.",
        },
        {
          name: "Paul Kruegel",
          role: "Advisor & Solo Founder",
          quote: "Chris has brought much-needed marketing intelligence and research to the Accelerator at Cointelegraph. He's a kind and smart listener who executes with confidence and a good sense of humor to keep presentations to clients or internal interesting. I am sure Chris will have a prosperous future, and he is a valuable asset to any marketing & intelligence team looking for a passionate data marketer with a kind and respectful demeanor.",
        },
        {
          name: "Michael Rhoden",
          role: "BD @TicTacc",
          quote: "Dynamically talented with both interpersonal and analytical skills, Chris played a crucial role in the Crypto Analytics and Communication team as a Communications Specialist. I had the pleasure of collaborating together with Chris on several projects simultaneously and always appreciated his go-getter and can-do attitude which he consistently brought to the team. I particularly appreciated Chris's seemingly effortless eye for detail and thoroughly enjoyed working with him. Chris would, without a doubt, be an asset to any team and could accomplish anything he sets his mind to.",
        },
      ],
      allRecommendationsCta: "View my recommendations on LinkedIn \u2192",
      allRecommendationsHref: "https://www.linkedin.com/in/krzysztofscibiorek/details/recommendations/",
    },
    whatsNext: {
      title: "Looking for a Growth Lead or a Strategy Lead?",
      body: "Open for senior remote roles. Available now.",
      ctaLinkedIn: "Message me on LinkedIn",
      ctaLinkedInHref: "https://www.linkedin.com/in/krzysztofscibiorek",
      ctaDownloadCV: "Download CV",
      ctaDownloadCVHref: "/cv/chris-scibiorek-cv-light.pdf",
      ctaDownloadCVDark: "prefer dark?",
      ctaDownloadCVDarkHref: "/cv/chris-scibiorek-cv-dark.pdf",
    },
    floatingCta: {
      label: "Connect",
      href: "https://www.linkedin.com/in/krzysztofscibiorek",
    },
  },

  legal: {
    cookieBanner: {
      message: "I use cookies to see how this site performs and improve it. Accept for full analytics, or reject. Either is fine.",
      privacyLink: "Privacy policy",
      accept: "Accept",
      reject: "Reject",
    },
    footer: {
      privacy: "Privacy",
    },
    privacy: {
      meta: {
        title: "Privacy Policy · Chris Scibiorek",
        description: "How data is collected, used, and protected on chrisworkflows.com.",
      },
      title: "Privacy Policy",
      lastUpdated: "Last updated: 2026-04-20",
      intro:
        "This page explains what data I collect when you visit chrisworkflows.com, how I use it, and the rights you have. Plain language, no legal fog.",
      sections: [
        {
          heading: "1. Who I am (data controller)",
          body: "Chris Scibiorek (Krzysztof Ścibiorek), operating as a sole proprietor (jednoosobowa działalność gospodarcza) based in Łódź, Poland. NIP: 5272991875. Contact: hello@chrisworkflows.com.",
        },
        {
          heading: "2. What I collect",
          body: "Three categories: (a) Analytics: anonymous page views, clicks, and session recordings via PostHog (EU-hosted). Recordings mask all form inputs. (b) Contact form and newsletter: name, email, optional company, and the question you send me. (c) Discovery calls: email and name you enter in Cal.com when booking a call.",
        },
        {
          heading: "3. Why I collect it",
          body: "Analytics: to see which pages work and which don't (legitimate interest plus your consent via the cookie banner). Contact form or newsletter: to answer you and, if you opt in, send occasional updates (consent: you submitted the form). Discovery calls: to hold the meeting (contract).",
        },
        {
          heading: "4. How it's stored and where",
          body: "PostHog: Frankfurt, EU (posthog.com/privacy). Web3Forms: processes contact form submissions. Cal.com: stores meeting bookings. iCloud and Gmail: I receive notifications in my inbox. No data is sold, ever.",
        },
        {
          heading: "5. How long I keep it",
          body: "PostHog analytics: up to 7 years (configurable retention). Contact form submissions: kept in email indefinitely unless you ask me to delete. Newsletter: until you unsubscribe.",
        },
        {
          heading: "6. Your rights",
          body: "You can: access what I have about you, correct it, delete it, object to processing, or withdraw consent at any time. Just email hello@chrisworkflows.com. I reply within 48h.",
        },
        {
          heading: "7. Cookies",
          body: "Only analytics cookies from PostHog (first-party, no third-party ad cookies). Full list and opt-in/out control via the cookie banner at the bottom of the page.",
        },
        {
          heading: "8. Changes to this policy",
          body: "I'll update the 'Last updated' date above when I change anything substantive. Check back occasionally.",
        },
      ],
      cookiePreferences: {
        heading: "Cookie preferences",
        body: "Changed your mind? Reset your choice and the cookie banner will reappear so you can accept or reject again.",
        button: "Reset cookie preferences",
        statusReset: "Preferences reset. The cookie banner is back at the bottom of the page.",
      },
    },
  },
};

export type Content = typeof en;
